import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Wrench, ListOrdered, Bot, Book, ChevronDown, LogOut, Settings, Notebook, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

type NavigationItem = {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
};

export type NavigationGuardCheck = (path: string) => boolean;

interface SidebarProps {
  onNavigationGuard?: NavigationGuardCheck;
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onNavigationGuard, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
    setIsCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (!isProfileOpen) {
      setIsCollapsed(true);
      setIsHovered(false);
    }
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  const { user, systemInfo, isAdmin } = useAuth();

  const navigationItems: NavigationItem[] = [
    { id: 'agents', label: 'Agents', icon: Bot, path: '/studio/agents' },
    { id: 'tools', label: 'Tools', icon: Wrench, path: '/studio/tools' },
    { id: 'playbooks', label: 'Playbooks', icon: Book, path: '/studio/playbooks' },
    { id: 'executions', label: 'Executions', icon: ListOrdered, path: '/studio/executions' },
    { id: 'chat', label: 'Chat', icon: MessageCircle, path: '/studio/chat' },
    { id: 'rag', label: 'RAG', icon: Notebook, path: '/studio/rag' },
  ];

  const isActivePath = (path: string): boolean => {
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string) => {
    if (onNavigationGuard) {
      if (!onNavigationGuard(path)) {
        return;
      }
    }
    navigate(path);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <div 
      className={`bg-gray-700 text-white h-screen relative flex flex-col transition-all duration-300 ease-out
        ${isCollapsed ? 'w-16' : 'w-64'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        transitionProperty: 'width, transform',
        transitionDuration: '200ms',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '2px 0 12px rgba(0, 0, 0, 0.1)'
      }}
    >

      <div 
        className={`p-4 ${isCollapsed ? 'px-3' : 'px-5'} flex-grow`}
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          setIsHovered(true);
        }}
      >
        {/* User Profile Section */}
        <div className={`mb-8 overflow-hidden ${isCollapsed ? 'text-center' : ''}`} ref={dropdownRef}>
          <div
            className={` mt-4 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-2 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors`}
            onClick={() => !isCollapsed && setIsProfileOpen(!isProfileOpen)}
          >
            <div className="flex items-center ">
              <div className={`relative bg-[#2c2c2e] rounded-[14px] overflow-hidden flex items-center justify-center`}
                style={{
                  width: isCollapsed ? '32px' : '36px',
                  height: isCollapsed ? '32px' : '36px',
                  backgroundColor: user?.profileImage ? 'transparent' : '#0a84ff',
                  transition: 'all 0.2s ease-out'
                }}>
                {user?.profileImage ? (
                  <>
                    <img
                      src={user.profileImage}
                      alt={`${user.firstName}'s profile`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallbackInitial = parent.querySelector('.fallback-initial');
                          if (fallbackInitial) {
                            (fallbackInitial as HTMLElement).style.display = 'flex';
                          }
                        }
                      }}
                    />
                    <div
                      className="fallback-initial absolute inset-0 hidden items-center justify-center text-white font-medium"
                      style={{ display: 'none' }}
                    >
                      {user?.firstName?.[0]?.toUpperCase() || user?.lastName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-medium">
                    {user?.firstName?.[0]?.toUpperCase() || user?.lastName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              {!isCollapsed && (
                <div className="ml-3 text-left flex items-center">
                  <div className="overflow-hidden">
                    <p className="text-[15px] font-semibold text-white whitespace-nowrap transform transition-all duration-200 ease-out tracking-tight"
                       style={{
                         transform: isCollapsed ? 'translateX(-10px)' : 'translateX(0)',
                         opacity: isCollapsed ? 0 : 1,
                         transitionDelay: isCollapsed ? '0ms' : '50ms',
                         fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                       }}>
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-[13px] text-gray-400 whitespace-nowrap transform transition-all duration-200 ease-out tracking-tight"
                       style={{
                         transform: isCollapsed ? 'translateX(-10px)' : 'translateX(0)',
                         opacity: isCollapsed ? 0 : 1,
                         transitionDelay: isCollapsed ? '0ms' : '75ms',
                         fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                       }}>
                      {user?.email}
                    </p>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`ml-2 text-gray-400 transition-all duration-200 ${isCollapsed ? 'opacity-0' : 'opacity-100'}`}
                    style={{
                      transitionDelay: isCollapsed ? '0ms' : '100ms'
                    }}
                  />
                </div>
              )}
            </div>
            {!isCollapsed && (
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform ${isProfileOpen ? 'transform rotate-180' : ''}`}
              />
            )}
          </div>

          {/* Dropdown Menu */}
          {isProfileOpen && (
            <div 
              className="absolute left-full mt-4 top-0 bg-gray-700 rounded-lg shadow-xl overflow-hidden z-50 min-w-[180px] border border-gray-600"
              onMouseEnter={() => {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                setIsProfileOpen(false);
                setIsCollapsed(true);
                setIsHovered(false);
              }}
            >

              <button
                onClick={() => {
                  handleNavigation('/studio/settings');
                  setIsProfileOpen(false);
                }}
                className="w-full flex items-center px-4 py-3 text-sm text-gray-300 hover:bg-gray-600 transition-colors"
              >
                <Settings size={16} className="mr-3" />
                Settings
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-3 text-sm text-gray-300 hover:bg-gray-600 transition-colors"
              >
                <LogOut size={16} className="mr-3" />
                Log Out
              </button>
            </div>
          )}
        </div>

        <nav className="space-y-2">
          {navigationItems.map(({ id, label, icon: Icon, path }, index) => (
            <React.Fragment key={id}>
              {id === 'executions' && (
                <div className={`my-2 border-t border-gray-700 ${isCollapsed ? 'mx-2' : 'mx-4'}`}></div>
              )}
              <button
                onClick={() => handleNavigation(path)}
                className={`w-full flex items-center rounded-lg transition-all duration-200
                  ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3 space-x-3'}
                  ${isActivePath(path)
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700'}`}
                title={isCollapsed ? label : undefined}
              >
                <Icon size={21} strokeWidth={2.2} className={`text-gray-300 ${isActivePath(path) ? 'text-white' : 'text-gray-400'}`} />
                <span 
                  className={`whitespace-nowrap transition-all duration-200 ease-out ${isCollapsed ? 'opacity-0 w-0 ml-0' : 'opacity-100 w-auto ml-4'}
                    text-[15px] font-medium tracking-tight`}
                  style={{
                    transform: isCollapsed ? 'translateX(-10px)' : 'translateX(0)',
                    display: isCollapsed ? 'inline-block' : 'inline-block',
                    transitionDelay: isCollapsed ? '0ms' : '50ms',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: isCollapsed ? '0' : '100%',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    color: isActivePath(path) ? '#ffffff' : '#ebebf5'
                  }}
                >
                  {label}
                </span>
              </button>
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Bottom section with Admin */}
      <div 
        className={`p-4 ${isCollapsed ? 'px-2' : ''}`}
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          setIsHovered(true);
        }}
      >
        {isAdmin && (
          <button
            onClick={() => handleNavigation('/studio/admin')}
            className={`w-full flex items-center rounded-lg transition-all duration-200 mb-4
              ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3 space-x-3'}
              ${isActivePath('/studio/admin') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
            title={isCollapsed ? 'Admin' : undefined}
          >
            <Settings size={21} strokeWidth={2.2} className="text-gray-300" />
            <span 
              className={`whitespace-nowrap transition-all duration-200 ease-out ${isCollapsed ? 'opacity-0 w-0 ml-0' : 'opacity-100 w-auto ml-3'}`}
              style={{
                transform: isCollapsed ? 'translateX(-10px)' : 'translateX(0)',
                display: isCollapsed ? 'inline-block' : 'inline-block',
                transitionDelay: isCollapsed ? '0ms' : '50ms',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: isCollapsed ? '0' : '100%'
              }}
            >
              Admin Panel
            </span>
          </button>
        )}

        <div className="border-t border-[#2c2c2e] pt-4 overflow-hidden">
          {systemInfo && (
            <div className={`mt-2 py-2 text-xs font-medium text-gray-500 text-center border-t border-[#2c2c2e] tracking-tight
              ${isCollapsed ? 'px-1' : 'px-2'}`}
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
              }}>
              <span 
                className={`whitespace-nowrap transition-all duration-200 ease-out ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}
                style={{
                  transform: isCollapsed ? 'translateX(-10px)' : 'translateX(0)',
                  display: 'inline-block',
                  transitionDelay: isCollapsed ? '0ms' : '50ms',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  maxWidth: isCollapsed ? '0' : '100%'
                }}
              >
                Version {systemInfo.version}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;